import { FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { saveErrorText } from "@/pages/Private/pages/Score/shell/saveErrorText";
import {
	useGetSearchDefaultsQuery,
	useRegenerateCompanySearchDefaultsMutation,
	useUpdateCompanySearchDefaultsMutation,
} from "@/pages/Private/redux/searchDefaults/searchDefaults.api";
import {
	PersonSearchDefaults,
	PersonSearchDefaultsSchema,
} from "@/pages/Private/redux/searchDefaults/searchDefaults.schema";
import { searchDefaultsSelector } from "@/pages/Private/redux/searchDefaults/searchDefaults.slice";
import { useAppSelector } from "@/redux/hooks";

import { SearchDefaultsFields } from "./SearchDefaultsFields";

const EMPTY_PRESET: PersonSearchDefaults = {
	positionQuery: [],
	seniority: [],
	department: [],
	countries: [],
	companyCountries: [],
	companyFilterSearch: { employees: [], industries: [] },
};

interface Notice {
	kind: "success" | "error";
	text: string;
}

/**
 * "Search defaults" on Company settings → General (PREC-3666): the company preset every new
 * person search opens with until a user has searched once. Admins edit it here or regenerate it
 * from the ICP and Lead Scoring; the tab is shown to COMPANY permission holders only, and the API
 * refuses both writes to anyone else.
 */
export const SearchDefaultsSettings: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.searchDefaults.settings.${key}`);

	useGetSearchDefaultsQuery();
	const { companyPreset, countryNames, loaded } = useAppSelector(searchDefaultsSelector);
	const [updatePreset, { isLoading: isSaving }] = useUpdateCompanySearchDefaultsMutation();
	const [regeneratePreset, { isLoading: isRegenerating }] =
		useRegenerateCompanySearchDefaultsMutation();
	const [notice, setNotice] = useState<Notice | null>(null);

	const { handleSubmit, reset, setValue, watch } = useForm<PersonSearchDefaults>({
		defaultValues: EMPTY_PRESET,
		resolver: zodResolver(PersonSearchDefaultsSchema),
	});
	const values = watch();

	// The stored preset — as loaded, saved or regenerated — is what the form shows.
	useEffect(() => {
		if (companyPreset) {
			reset(companyPreset);
		}
	}, [companyPreset, reset]);

	const onSave = handleSubmit(async (preset) => {
		setNotice(null);

		try {
			await updatePreset(preset).unwrap();
			setNotice({ kind: "success", text: ts("saved") });
		} catch (error) {
			setNotice({ kind: "error", text: saveErrorText(error, ts("saveFailed")) });
		}
	});

	const onRegenerate = async () => {
		setNotice(null);

		try {
			await regeneratePreset().unwrap();
			setNotice({ kind: "success", text: ts("regenerated") });
		} catch (error) {
			setNotice({ kind: "error", text: saveErrorText(error, ts("regenerateFailed")) });
		}
	};

	return (
		<div
			className="rounded-xl border border-border-light bg-white p-6"
			data-testid="search-defaults-settings"
		>
			<h3 className="text-md font-bold">{ts("title")}</h3>
			<p className="mt-1 max-w-[680px] text-ssm text-text-meta">{ts("description")}</p>

			{!loaded ? (
				<p className="mt-4 text-ssm text-text-meta">{ts("loading")}</p>
			) : (
				<div className="mt-4 flex flex-col gap-4">
					<SearchDefaultsFields countryNames={countryNames} setValue={setValue} values={values} />

					{notice && (
						<p
							className={
								notice.kind === "error"
									? "text-ssm text-status-error"
									: "text-ssm text-status-success"
							}
							data-testid="search-defaults-notice"
							role={notice.kind === "error" ? "alert" : "status"}
						>
							{notice.text}
						</p>
					)}

					<div className="flex flex-col gap-3 tb:flex-row tb:items-center tb:justify-between">
						<p className="max-w-[520px] text-xs text-text-meta">{ts("regenerateHint")}</p>
						<div className="flex shrink-0 gap-2">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={isSaving}
								isLoading={isRegenerating}
								size={ButtonSize.S}
								testId="search-defaults-regenerate"
								title={ts("regenerate")}
								onClick={onRegenerate}
							/>
							<Button
								color={ButtonColor.ACTION}
								disabled={isRegenerating}
								isLoading={isSaving}
								size={ButtonSize.S}
								testId="search-defaults-save"
								title={ts("save")}
								onClick={onSave}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
